/* Offline write queue (IndexedDB) + sync engine.
 * - When an API write fails due to network, the request is queued.
 * - On 'online' event or app start, queued requests are replayed.
 */
import { openDB } from "idb";

const DB_NAME = "wsdk-offline";
const STORE = "queue";
const CACHE_STORE = "read_cache";

async function db() {
  return openDB(DB_NAME, 1, {
    upgrade(d) {
      if (!d.objectStoreNames.contains(STORE)) {
        const s = d.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
        s.createIndex("createdAt", "createdAt");
      }
      if (!d.objectStoreNames.contains(CACHE_STORE)) {
        d.createObjectStore(CACHE_STORE);
      }
    },
  });
}

export async function enqueue(item) {
  const d = await db();
  const id = await d.add(STORE, {
    method: item.method,
    url: item.url,
    data: item.data ?? null,
    headers: item.headers ?? null,
    createdAt: new Date().toISOString(),
    attempts: 0,
  });
  notifyChange();
  return id;
}

export async function listQueue() {
  const d = await db();
  return d.getAll(STORE);
}

export async function queueCount() {
  const d = await db();
  return d.count(STORE);
}

export async function dequeue(id) {
  const d = await db();
  await d.delete(STORE, id);
  notifyChange();
}

export async function markAttempt(id) {
  const d = await db();
  const tx = d.transaction(STORE, "readwrite");
  const item = await tx.store.get(id);
  if (item) {
    item.attempts = (item.attempts || 0) + 1;
    await tx.store.put(item);
  }
  await tx.done;
}

// Cache helpers for offline reads (JSON data)
export async function cachePut(key, value) {
  const d = await db();
  await d.put(CACHE_STORE, value, key);
}

export async function cacheGet(key) {
  const d = await db();
  return d.get(CACHE_STORE, key);
}

// Pub/sub for UI updates
const listeners = new Set();
export function onQueueChange(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
function notifyChange() {
  listeners.forEach((cb) => {
    try { cb(); } catch (_) { /* noop */ }
  });
}

// Replay queue when back online.
// `axios` is the configured instance with baseURL and interceptors.
let syncing = false;
export async function syncQueue(axios) {
  if (syncing) return { synced: 0, failed: 0 };
  if (!navigator.onLine) return { synced: 0, failed: 0 };
  syncing = true;
  let synced = 0, failed = 0;
  try {
    const items = await listQueue();
    items.sort((a, b) => a.id - b.id);
    for (const it of items) {
      try {
        const cfg = { method: it.method, url: it.url, data: it.data || undefined };
        if (it.headers) cfg.headers = it.headers;
        const resp = await axios.request(cfg);
        if (resp && resp.status >= 200 && resp.status < 300) {
          await dequeue(it.id);
          synced++;
        } else {
          await markAttempt(it.id);
          failed++;
        }
      } catch (e) {
        // Network or server error — keep in queue (unless 4xx which is a hard fail)
        const status = e.response?.status;
        if (status && status >= 400 && status < 500) {
          // Client error: drop to avoid infinite retries
          await dequeue(it.id);
          failed++;
        } else {
          await markAttempt(it.id);
          failed++;
          // Stop attempting further if offline now
          if (!navigator.onLine) break;
        }
      }
    }
  } finally {
    syncing = false;
    notifyChange();
  }
  return { synced, failed };
}
